import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { useRootStore } from 'stores/RootStore'
import { BrandKitCreationModalProps } from 'components/BrandKitCreationModal/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useMachine } from '@xstate/react'
import { brandKitCreationMachine } from 'components/BrandKitCreation/brandKitCreationMachine'
import React, { useCallback, useState } from 'react'
import { BrandKit, FontRole } from 'types'
import { EventId, StateId } from 'components/BrandKitCreation/consts'
import { shuffle } from 'lodash'
import { PALETTES } from 'constants/palettes'
import { BrandKitInstructions } from './BrandKitInstructions'
import { BrandKitParent } from 'flows/common/steps/BrandKitForm/BrandKitParent'
import { BrandKitColorsAndFonts } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitColorsAndFonts'
import { BrandKitImages } from 'flows/common/steps/BrandKitImages/BrandKitImages'
import { BrandKitVoice } from 'flows/common/steps/BrandKitVoice/BrandKitVoice'
import { BrandKitFinished } from 'flows/common/steps/BrandKitFinished/BrandKitFinished'
import { getBrandKitFontPairing } from 'hooks/queries/brandKits/getBrandKitFontPairing'
import { generatePath, useHistory } from 'react-router-dom'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BRAND_KIT_PATH } from 'paths'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { WizardsDrawer } from 'components/Wizards/WizardsDrawer/WizardsDrawer'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ColorPaletteState, FontPairingState } from 'components/FirstTimeExperienceModal/Types'
import { FontPairingStyle } from 'constants/fontPairings'

interface BrandKitCreationDrawerProps extends BrandKitCreationModalProps, DrawerProps {
  isRegFlow?: boolean | null
}

export const BrandKitCreationDrawer = (props: BrandKitCreationDrawerProps) => {
  const { isIOSNative } = useResponsiveDesign()
  const workspaceId = useCurrentWorkspaceId()
  const { drawersStore } = useRootStore()
  const { closeDrawer, closeAllDrawers, openDrawer } = drawersStore
  const { isRegFlow } = props
  const history = useHistory()
  const [current, send] = useMachine(brandKitCreationMachine, {
    context: {
      brandKit: null,
    },
  })

  const [brandKitId, setBrandKitId] = useState<number | undefined>(undefined)

  const { data } = useBrandKit(brandKitId)

  const [colorPaletteState, setColorPaletteState] = React.useState<ColorPaletteState>({
    websitePalette: [],
    generatedPalettes: [],
    selectedPaletteIndex: 0,
  })

  const [fontPairingState, setFontPairingState] = React.useState<FontPairingState>({
    websitePairing: null,
  })

  const brandKit = data?.brand_kit

  const handleSkip = () => {
    sendMessageToNativeApp('brandKitSkip')
  }

  const handleBack = useCallback(() => {
    if (current.matches(StateId.Instructions)) {
      if (isIOSNative) {
        sendMessageToNativeApp('closeButtonPressed')
      } else {
        closeDrawer()
      }
    }
    send(EventId.Back)
  }, [current, send, isIOSNative, closeDrawer])

  const handleNext = useCallback(() => send(EventId.Next), [send])

  const handleFinished = useCallback(() => {
    closeAllDrawers()
    openDrawer(WizardsDrawer, { initialValues: { brand_kit_id: brandKit?.id } })
  }, [closeAllDrawers, openDrawer, brandKit?.id])

  const navigationProps = {
    onNext: handleNext,
    onBack: handleBack,
  }

  const handleBrandKitGenerated = useCallback((brandKit: BrandKit) => {
    // Save the original website palette
    const websitePalette = brandKit.brand_visual_kit?.primary_color_palette?.colors.map((color) => color.hex) || []

    // Generate random palettes at this point
    const generatedPalettes = shuffle(PALETTES).slice(0, 3)

    setColorPaletteState({
      websitePalette,
      generatedPalettes,
      selectedPaletteIndex: 0,
    })

    // Save the original website fonts
    let websitePairing = null

    if (brandKit.brand_visual_kit) {
      websitePairing = getBrandKitFontPairing(brandKit.brand_visual_kit) || null
    }

    setFontPairingState({
      websitePairing,
    })

    setBrandKitId(brandKit.id)
  }, [])

  return (
    <Drawer.Root {...props}>
      <Drawer.Portal>
        <Drawer.Content fullHeight disableAnimation={isIOSNative}>
          <Drawer.InnerContent isSnappedTop>
            <Drawer.Top>
              {current.matches(StateId.Instructions) || current.matches(StateId.Finished) ? (
                <div />
              ) : (
                <Drawer.Back variant={isIOSNative ? 'editor' : 'ghost'} onPress={handleBack} />
              )}
              <div />
              <Drawer.Title asChild>
                <VisuallyHidden>
                  <Heading level={5}>Create Brand Kit</Heading>
                </VisuallyHidden>
              </Drawer.Title>
              {isRegFlow && isIOSNative ? (
                <>
                  <div />
                  <Drawer.Back variant="editor" position="right" onPress={handleSkip}>
                    Skip
                  </Drawer.Back>
                </>
              ) : (
                <Drawer.Close position="right" />
              )}
            </Drawer.Top>
            <Drawer.Container padding="0px">
              {current.matches(StateId.Instructions) && <BrandKitInstructions onNext={handleNext} />}
              {current.matches(StateId.Form) && (
                <BrandKitParent
                  {...navigationProps}
                  onBrandKitGenerated={handleBrandKitGenerated}
                  useIntegrations={false}
                />
              )}
              {current.matches(StateId.Colors) && !!brandKit && (
                <BrandKitColorsAndFonts
                  title="Boom! Let's confirm your new Brand Kit ✨"
                  {...navigationProps}
                  brandKit={brandKit}
                  colorPaletteState={colorPaletteState}
                  setColorPaletteState={setColorPaletteState}
                  fontPairingState={fontPairingState}
                />
              )}
              {current.matches(StateId.Images) && !!brandKit && (
                <BrandKitImages {...navigationProps} brandKit={brandKit} title="Add images and video" />
              )}
              {current.matches(StateId.Voice) && !!brandKit && (
                <BrandKitVoice {...navigationProps} brandKit={brandKit} title="Review your Brand Voice" />
              )}
              {current.matches(StateId.Finished) && !!brandKit && (
                <BrandKitFinished
                  title="Bam! Here’s your finished Brand Kit!"
                  onBack={handleBack}
                  selectedFontPairing={[
                    {
                      font_role: FontRole.Title,
                      style: brandKit.brand_visual_kit?.text_styles?.find((style) => style.font_role === FontRole.Title)
                        ?.style,
                    } as FontPairingStyle<FontRole.Title>,
                    {
                      font_role: FontRole.Body,
                      style: brandKit.brand_visual_kit?.text_styles?.find((style) => style.font_role === FontRole.Body)
                        ?.style,
                    } as FontPairingStyle<FontRole.Body>,
                  ]}
                  onNext={handleFinished}
                  onOpenBrandKit={(brandKit) => {
                    history.push(generatePath(BRAND_KIT_PATH, { id: brandKit.id, workspaceId }))
                    closeAllDrawers()
                  }}
                  brandKit={brandKit}
                  showHomeButton={!!isRegFlow && isIOSNative}
                />
              )}
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
        <Drawer.Overlay />
      </Drawer.Portal>
    </Drawer.Root>
  )
}
